import React, { useEffect, useState } from 'react';
import { Code } from '@mui/icons-material';
import { Box, IconButton, Tooltip, Typography, useTheme } from '@mui/material';

import { useSessionContext } from '../../../contexts/SessionContext';
import { useCopilotContext } from '../../../contexts/CopilotContext';
import { useBookingsContext } from '../../../contexts/BookingsContext';

import Images from '../../../components/Images';
import Chat from '../../../components/Chat';
import Transcript from '../../../components/Transcript';
import Copilot from '../../../components/Copilot';

import { COLORS } from '../../../utils/colors';
import { useInspectorStyles } from '../styles';
import aiSearchIcon from '../../../utils/assets/ai_search.svg';
import streetviewIcon from '../../../utils/assets/streetview.png';
import HomeOwnerNotes from '../../../components/HomeownerNotes';
import GoogleMaps from '../../../components/GoogleMaps';
import ChatIcon from '../../../icons/ChatIcon';
import HomeOwnerNotesIcon from '../../../icons/HomeOwnerNotesIcon';
import ImagesIcon from '../../../icons/ImagesIcon';
import MapsIcon from '../../../icons/MapsIcon';
import AIRenderer from '../../../components/AIRenderer';
import AIRenderIcon from '../../../icons/AIRenderIcon';
import TranscriptIcon from '../../../icons/TranscriptIcon';

const Panels = () => {
  const theme = useTheme();
  const styles = useInspectorStyles();
  const [activeTab, setActiveTab] = useState('Appointment Description');
  const [copilotHeight, setCopilotHeight] = useState<number>(25);
  const { userSettings } = useBookingsContext();
  const {
    sipSessionId,
    hasUnreadChat,
    setHasUnreadChat,
    hasUpdatedImages,
    setHasUpdatedImages,
    remoteParticipant,
  } = useSessionContext();

  const { copilotEnabled, setCopilotEnabled } = useCopilotContext();

  const handleSetActiveTab = (label: string) => {
    if (label === 'Copilot') {
      setCopilotEnabled(!copilotEnabled);
      return;
    }

    setActiveTab(label);
    if (label === 'Chat') {
      setHasUnreadChat(false);
    }
    if (label === 'Images') {
      setHasUpdatedImages(false);
    }
  };

  useEffect(() => {
    if (remoteParticipant || sipSessionId) setActiveTab('Transcripts');
  }, [remoteParticipant, sipSessionId]);

  const RIGHT_SIDEBAR_ICONS = [
    ...(userSettings?.dev
      ? [
          {
            Icon: Code,
            label: 'Copilot',
            viewTitle: 'AI Search',
            view: true,
          },
        ]
      : []),

    {
      Icon: TranscriptIcon,
      label: 'Transcripts',
      viewTitle: 'Transcript',
      view: <Transcript useTabs={true} viewTitle={'Transcripts'} />,
    },
    {
      Icon: ImagesIcon,
      label: 'Images',
      viewTitle: 'Images',
      view: <Images viewTitle={'Images'} />,
    },
    {
      Icon: HomeOwnerNotesIcon,
      label: 'Appointment Description',
      viewTitle: 'Appointment Description',
      view: (
        <HomeOwnerNotes useTabs={true} viewTitle={'Appointment Description'} />
      ),
    },
    ...(userSettings?.dev
      ? [
          {
            Icon: AIRenderIcon,
            label: 'AI Render',
            viewTitle: 'AI Render',
            view: <AIRenderer viewTitle={'AI Render'} />,
          },
        ]
      : []),
    {
      Icon: MapsIcon,
      label: 'Map',
      viewTitle: 'Map',
      view: <GoogleMaps viewTitle={'Map'} />,
    },
  ];

  useEffect(() => {
    if (hasUnreadChat && activeTab === 'Chat') {
      setHasUnreadChat(false);
    }

    if (hasUpdatedImages && activeTab === 'Images') {
      setHasUpdatedImages(false);
    }
  }, [
    activeTab,
    hasUnreadChat,
    hasUpdatedImages,
    setHasUnreadChat,
    setHasUpdatedImages,
  ]);

  return (
    <Box sx={styles.view}>
      <Box sx={styles.vhcInfo}>
        {copilotEnabled && (
          <Copilot
            viewTitle={'Dev Copilot'}
            copilotHeight={copilotHeight}
            setCopilotHeight={setCopilotHeight}
          />
        )}
        {RIGHT_SIDEBAR_ICONS.map(({ label, view, viewTitle }, index) => {
          const active = label === activeTab && view;
          return !active ? (
            <></>
          ) : (
            <Box
              key={index}
              height={`${copilotEnabled ? 100 - copilotHeight : 100}%`}>
              {view}
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          ...styles.sidebar,
          boxShadow: '0 2px 9px 0 rgba(0, 0, 0, 0.15)',
          bgcolor: COLORS.WHITE,
        }}>
        {RIGHT_SIDEBAR_ICONS.map(({ Icon, label, view }, index) => {
          const isDisabled =
            !view || (label === 'Copilot' && activeTab !== 'Transcripts');
          const active =
            activeTab === label || (label === 'Copilot' && copilotEnabled);
          const bgColor =
            active && label !== 'Copilot' ? '#FFE0CC' : COLORS.WHITE;

          return (
            <Box
              key={index}
              sx={{
                ...styles.sidebarRightButton,
                color: active ? '#ff6e00' : 'black',
              }}
              bgcolor={bgColor}
              borderColor={
                label === 'Transcripts'
                  ? `${theme.palette.primary}`
                  : 'transparent'
              }>
              <Tooltip
                componentsProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: '#FF6E00',
                      color: COLORS.WHITE,
                      fontSize: '0.875rem',
                      borderRadius: '8px',
                      padding: '.5rem .75rem',
                      boxShadow: 3,
                    },
                  },
                }}
                title={label}>
                <IconButton
                  id={`_${label.toLowerCase()}`}
                  disabled={isDisabled}
                  disableRipple
                  onClick={() => handleSetActiveTab(label)}
                  color="inherit">
                  {((label === 'Chat' && hasUnreadChat) ||
                    (label === 'Images' && hasUpdatedImages)) && (
                    <Box sx={styles.unreadMessageIndicator} />
                  )}

                  {label === 'AI Search' || label === 'Streetview' ? (
                    <img
                      alt={label}
                      src={
                        label === 'AI Search' ? aiSearchIcon : streetviewIcon
                      }
                      style={{
                        objectFit: 'contain',
                        opacity: isDisabled ? 0.5 : 1,
                        width: '2rem',
                        height: '2rem',
                      }}
                    />
                  ) : (
                    <Icon sx={{ fontSize: '29px' }} />
                  )}
                </IconButton>
              </Tooltip>
              {/* <Typography
                sx={{
                  opacity: isDisabled ? 0.5 : 1,
                  fontSize: '.625rem',
                  textAlign: 'center',
                }}>
                {label}
              </Typography> */}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default Panels;
